
import React, { useState } from "react";
import useCompanyCapital from "../../hooks/useCompanyCapital";
import SummaryCards from "../SummaryCards/SummaryCards";
import CapitalTable from "./CapitalTable";
import CapitalFormModal from "./CapitalFormModal";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { CAPITAL_TYPE_OPTIONS } from "../../constants/capitalTypes";
import styles from "./CapitalTab.module.css";

const MONTHS = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
];

const CapitalTab = () => {
  const {
    items,
    summary,
    filters,
    pagination,
    loading,
    updateFilters,
    goToPage,
    addItem,
    editItem,
    deleteItem,
  } = useCompanyCapital();

  // =====================================================
  // Capital Modal
  // =====================================================

  const [modalOpen, setModalOpen] =
    useState(false);

  const [selectedItem, setSelectedItem] =
    useState(null);

  // =====================================================
  // Delete
  // =====================================================

  const [itemToDelete, setItemToDelete] =
    useState(null);

  const [deleting, setDeleting] =
    useState(false);

  // =====================================================
  // Date Filter
  // =====================================================

  const [showDateFilter, setShowDateFilter] =
    useState(false);

  /*
   * day | month | year
   */
  const [filterType, setFilterType] =
    useState("day");

  /*
   * From | To
   */
  const [selectingField, setSelectingField] =
    useState("From");

  /*
   * التاريخ المؤكد
   */
  const [fromDate, setFromDate] =
    useState(null);

  const [toDate, setToDate] =
    useState(null);

  /*
   * التاريخ المؤقت داخل Modal
   */
  const [tempDate, setTempDate] =
    useState(null);

  const [selectedYear, setSelectedYear] =
    useState(new Date().getFullYear());

  const [selectedMonth, setSelectedMonth] =
    useState(new Date().getMonth());

  const [selectedDay, setSelectedDay] =
    useState(new Date().getDate());

  // =====================================================
  // Helpers
  // =====================================================

  const pad = (value) =>
    String(value).padStart(2, "0");

  const formatDisplayDate = (date) => {
    if (!date) return "";

    return `${pad(date.getDate())}/${pad(
      date.getMonth() + 1
    )}/${date.getFullYear()}`;
  };

  const formatApiStart = (date) => {
    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T00:00:00`;
  };

  const formatApiEnd = (date) => {
    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T23:59:59`;
  };

  // =====================================================
  // Capital Modal
  // =====================================================

  const openAddModal = () => {
    setSelectedItem(null);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  const handleSubmit = async (
    payload,
    setFieldError
  ) => {
    if (selectedItem) {
      await editItem(
        selectedItem.id,
        payload,
        setFieldError
      );
    } else {
      await addItem(
        payload,
        setFieldError
      );
    }
  };

  // =====================================================
  // Delete
  // =====================================================

  const requestDelete = (item) => {
    setItemToDelete(item);
  };

  const cancelDelete = () => {
    if (deleting) return;

    setItemToDelete(null);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    setDeleting(true);

    try {
      await deleteItem(
        itemToDelete.id
      );

      setItemToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // =====================================================
  // Open Date Modal
  // =====================================================

  const openDateFilter = (field) => {
    setSelectingField(field);

    const currentDate =
      field === "From"
        ? fromDate
        : toDate;

    const dateToUse =
      currentDate || new Date();

    const copiedDate = new Date(
      dateToUse
    );

    setTempDate(copiedDate);

    setSelectedYear(
      copiedDate.getFullYear()
    );

    setSelectedMonth(
      copiedDate.getMonth()
    );

    setSelectedDay(
      copiedDate.getDate()
    );

    setShowDateFilter(true);
  };

  // =====================================================
  // Temporary Date
  // =====================================================

  const selectTempDate = (date) => {
    if (!date) return;

    setTempDate(date);

    setSelectedYear(
      date.getFullYear()
    );

    setSelectedMonth(
      date.getMonth()
    );

    setSelectedDay(
      date.getDate()
    );
  };

  // =====================================================
  // Day
  // =====================================================

  const handleSelectDay = (day) => {
    const date = new Date(
      selectedYear,
      selectedMonth,
      day
    );

    selectTempDate(date);
  };

  // =====================================================
  // Month
  // =====================================================

  const handleSelectMonth = (
    monthIndex
  ) => {
    let date;

    if (selectingField === "From") {
      /*
       * أول يوم في الشهر
       */
      date = new Date(
        selectedYear,
        monthIndex,
        1
      );
    } else {
      /*
       * آخر يوم في الشهر
       */
      date = new Date(
        selectedYear,
        monthIndex + 1,
        0
      );
    }

    selectTempDate(date);
  };

  // =====================================================
  // Year
  // =====================================================

  const handleSelectYear = (year) => {
    let date;

    if (selectingField === "From") {
      /*
       * أول يوم في السنة
       */
      date = new Date(
        year,
        0,
        1
      );
    } else {
      /*
       * آخر يوم في السنة
       */
      date = new Date(
        year,
        11,
        31
      );
    }

    selectTempDate(date);
  };

  // =====================================================
  // Confirm Date
  // =====================================================

  const confirmDateSelection = () => {
    if (!tempDate) return;

    if (selectingField === "From") {
      setFromDate(
        new Date(tempDate)
      );
    } else {
      setToDate(
        new Date(tempDate)
      );
    }

    /*
     * هنا فقط يقفل الـ Modal.
     */
    setShowDateFilter(false);
  };

  // =====================================================
  // Cancel Date
  // =====================================================

  const cancelDateSelection = () => {
    setShowDateFilter(false);
  };

  // =====================================================
  // Apply Date Filter
  // =====================================================

  const applyDateFilter = () => {
    if (!fromDate || !toDate) {
      alert(
        "من فضلك اختر التاريخ من وإلى"
      );

      return;
    }

    if (fromDate > toDate) {
      alert(
        "تاريخ البداية يجب أن يكون قبل تاريخ النهاية"
      );

      return;
    }

    /*
     * مهم:
     * بنبعت From و To فقط هنا.
     */
    updateFilters({
      From: formatApiStart(
        fromDate
      ),

      To: formatApiEnd(
        toDate
      ),
    });
  };

  // =====================================================
  // Clear Date Filter
  // =====================================================

  const clearDateFilter = () => {
    setFromDate(null);
    setToDate(null);
    setTempDate(null);

    updateFilters({
      From: "",
      To: "",
    });
  };

  // =====================================================
  // Calendar
  // =====================================================

  const daysInMonth =
    new Date(
      selectedYear,
      selectedMonth + 1,
      0
    ).getDate();

  const currentYear =
    new Date().getFullYear();

  /*
   * آخر 5 سنوات
   */
  const years = Array.from(
    { length: 5 },
    (_, index) =>
      currentYear - index
  );

  // =====================================================
  // Render
  // =====================================================

  return (
    <div
      className={styles.wrapper}
      dir="rtl"
    >
      {/* =================================================
          SUMMARY
      ================================================== */}

      <SummaryCards
        summary={summary}
      />

      {/* =================================================
          TOOLBAR
      ================================================== */}

      <div
        className={styles.toolbar}
      >
        <div
          className={styles.filters}
        >
          {/* Type */}

          <select
            value={filters.Type}
            onChange={(e) =>
              updateFilters({
                Type: e.target.value,
              })
            }
          >
            <option value="">
              كل الأنواع
            </option>

            {CAPITAL_TYPE_OPTIONS.map(
              (opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                >
                  {opt.label}
                </option>
              )
            )}
          </select>

          {/* From */}

          <button
            type="button"
            className={
              styles.dateButton
            }
            onClick={() =>
              openDateFilter(
                "From"
              )
            }
          >
            {fromDate
              ? formatDisplayDate(
                  fromDate
                )
              : "من تاريخ"}
          </button>

          {/* To */}

          <button
            type="button"
            className={
              styles.dateButton
            }
            onClick={() =>
              openDateFilter(
                "To"
              )
            }
          >
            {toDate
              ? formatDisplayDate(
                  toDate
                )
              : "إلى تاريخ"}
          </button>

          {/* Search */}

          <input
            type="text"
            placeholder="بحث..."
            value={
              filters.SearchTerm
            }
            onChange={(e) =>
              updateFilters({
                SearchTerm:
                  e.target.value,
              })
            }
          />

          {/* Date Actions */}

          <button
            type="button"
            className={
              styles.applyFilterBtn
            }
            onClick={
              applyDateFilter
            }
          >
            تطبيق
          </button>

          <button
            type="button"
            className={
              styles.clearFilterBtn
            }
            onClick={
              clearDateFilter
            }
          >
            مسح
          </button>
        </div>

        {/* Add */}

        <button
          className={
            styles.addBtn
          }
          onClick={
            openAddModal
          }
        >
          + إضافة رأس مال
        </button>
      </div>

      {/* =================================================
          SELECTED RANGE
      ================================================== */}

      <div
        className={
          styles.selectedRange
        }
      >
        <span>
          الفترة المختارة:
        </span>

        <strong>
          {fromDate
            ? formatDisplayDate(
                fromDate
              )
            : "غير محدد"}

          <span
            className={
              styles.arrow
            }
          >
            {" → "}
          </span>

          {toDate
            ? formatDisplayDate(
                toDate
              )
            : "غير محدد"}
        </strong>
      </div>

      {/* =================================================
          TABLE
      ================================================== */}

      <CapitalTable
        items={items}
        loading={loading}
        onEdit={openEditModal}
        onDelete={requestDelete}
      />

      {/* =================================================
          PAGINATION
      ================================================== */}

      {pagination.totalPages > 1 && (
        <div
          className={
            styles.pagination
          }
        >
          <button
            disabled={
              !pagination.hasPreviousPage
            }
            onClick={() =>
              goToPage(
                pagination.currentPage -
                  1
              )
            }
          >
            السابق
          </button>

          <span>
            صفحة{" "}
            {pagination.currentPage}{" "}
            من{" "}
            {pagination.totalPages}
          </span>

          <button
            disabled={
              !pagination.hasNextPage
            }
            onClick={() =>
              goToPage(
                pagination.currentPage +
                  1
              )
            }
          >
            التالي
          </button>
        </div>
      )}

      {/* =================================================
          CAPITAL FORM
      ================================================== */}

      <CapitalFormModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialData={selectedItem}
      />

      {/* =================================================
          DELETE
      ================================================== */}

      <ConfirmDialog
        isOpen={!!itemToDelete}
        title="تأكيد الحذف"
        message={`هل أنت متأكد من حذف "${
          itemToDelete?.title ||
          itemToDelete?.name ||
          "هذا العنصر"
        }"؟ لا يمكن التراجع عن هذا الإجراء.`}
        loading={deleting}
        onConfirm={
          confirmDelete
        }
        onCancel={
          cancelDelete
        }
      />

      {/* =================================================
          DATE MODAL
      ================================================== */}

      {showDateFilter && (
        <div
          className={
            styles.modalOverlay
          }
          onClick={
            cancelDateSelection
          }
        >
          <div
            className={styles.modal}
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            {/* ===========================================
                HEADER
            ============================================ */}

            <div
              className={
                styles.modalHeader
              }
            >
              <div>
                <h3>
                  اختيار{" "}
                  {selectingField ===
                  "From"
                    ? "تاريخ البداية"
                    : "تاريخ النهاية"}
                </h3>

                <p>
                  اختر اليوم أو الشهر أو
                  السنة ثم اضغط تأكيد
                </p>
              </div>

              <button
                type="button"
                className={
                  styles.closeButton
                }
                onClick={
                  cancelDateSelection
                }
              >
                ×
              </button>
            </div>

            {/* ===========================================
                TABS
            ============================================ */}

            <div
              className={
                styles.typeTabs
              }
            >
              <button
                type="button"
                className={
                  filterType === "day"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType(
                    "day"
                  )
                }
              >
                يوم
              </button>

              <button
                type="button"
                className={
                  filterType === "month"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType(
                    "month"
                  )
                }
              >
                شهر
              </button>

              <button
                type="button"
                className={
                  filterType === "year"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType(
                    "year"
                  )
                }
              >
                سنة
              </button>
            </div>

            {/* ===========================================
                DAY
            ============================================ */}

            {filterType === "day" && (
              <>
                <div
                  className={
                    styles.calendarHeader
                  }
                >
                  <button
                    type="button"
                    onClick={() => {
                      const previous =
                        new Date(
                          selectedYear,
                          selectedMonth -
                            1,
                          1
                        );

                      setSelectedYear(
                        previous.getFullYear()
                      );

                      setSelectedMonth(
                        previous.getMonth()
                      );
                    }}
                  >
                    ‹
                  </button>

                  <strong>
                    {
                      MONTHS[
                        selectedMonth
                      ]
                    }{" "}
                    {selectedYear}
                  </strong>

                  <button
                    type="button"
                    onClick={() => {
                      const next =
                        new Date(
                          selectedYear,
                          selectedMonth +
                            1,
                          1
                        );

                      setSelectedYear(
                        next.getFullYear()
                      );

                      setSelectedMonth(
                        next.getMonth()
                      );
                    }}
                  >
                    ›
                  </button>
                </div>

                <div
                  className={
                    styles.dayGrid
                  }
                >
                  {Array.from(
                    {
                      length:
                        daysInMonth,
                    },
                    (_, index) => {
                      const day =
                        index + 1;

                      const isSelected =
                        tempDate &&
                        tempDate.getFullYear() ===
                          selectedYear &&
                        tempDate.getMonth() ===
                          selectedMonth &&
                        tempDate.getDate() ===
                          day;

                      return (
                        <button
                          key={day}
                          type="button"
                          className={
                            isSelected
                              ? `${styles.dayButton} ${styles.selected}`
                              : styles.dayButton
                          }
                          onClick={() =>
                            handleSelectDay(
                              day
                            )
                          }
                        >
                          {day}
                        </button>
                      );
                    }
                  )}
                </div>
              </>
            )}

            {/* ===========================================
                MONTH
            ============================================ */}

            {filterType === "month" && (
              <>
                <div
                  className={
                    styles.calendarHeader
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedYear(
                        (prev) =>
                          prev - 1
                      )
                    }
                  >
                    ‹
                  </button>

                  <strong>
                    {selectedYear}
                  </strong>

                  <button
                    type="button"
                    onClick={() =>
                      setSelectedYear(
                        (prev) =>
                          prev + 1
                      )
                    }
                  >
                    ›
                  </button>
                </div>

                <div
                  className={
                    styles.monthGrid
                  }
                >
                  {MONTHS.map(
                    (
                      month,
                      index
                    ) => {
                      const isSelected =
                        tempDate &&
                        tempDate.getFullYear() ===
                          selectedYear &&
                        tempDate.getMonth() ===
                          index;

                      return (
                        <button
                          key={month}
                          type="button"
                          className={
                            isSelected
                              ? `${styles.monthButton} ${styles.selected}`
                              : styles.monthButton
                          }
                          onClick={() =>
                            handleSelectMonth(
                              index
                            )
                          }
                        >
                          {month}
                        </button>
                      );
                    }
                  )}
                </div>
              </>
            )}

            {/* ===========================================
                YEAR
            ============================================ */}

            {filterType === "year" && (
              <div
                className={
                  styles.yearGrid
                }
              >
                {years.map(
                  (year) => {
                    const isSelected =
                      tempDate &&
                      tempDate.getFullYear() ===
                        year;

                    return (
                      <button
                        key={year}
                        type="button"
                        className={
                          isSelected
                            ? `${styles.yearButton} ${styles.selected}`
                            : styles.yearButton
                        }
                        onClick={() =>
                          handleSelectYear(
                            year
                          )
                        }
                      >
                        {year}
                      </button>
                    );
                  }
                )}
              </div>
            )}

            {/* ===========================================
                FOOTER
            ============================================ */}

            <div
              className={
                styles.modalFooter
              }
            >
              <div
                className={
                  styles.tempSelectedDate
                }
              >
                <span>
                  الاختيار الحالي:
                </span>

                <strong>
                  {tempDate
                    ? formatDisplayDate(
                        tempDate
                      )
                    : "لم يتم الاختيار"}
                </strong>
              </div>

              <div
                className={
                  styles.modalActions
                }
              >
                <button
                  type="button"
                  className={
                    styles.cancelModalButton
                  }
                  onClick={
                    cancelDateSelection
                  }
                >
                  إلغاء
                </button>

                <button
                  type="button"
                  className={
                    styles.confirmModalButton
                  }
                  disabled={!tempDate}
                  onClick={
                    confirmDateSelection
                  }
                >
                  تأكيد
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapitalTab;
